/**
 * WGVisual Component - Working Groups Visual Hierarchy Display with Lazy Loading - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Complex visual hierarchy component displaying working groups, steering groups, and their
 * relationships with interactive preview modals. Features dynamic styling, arrow indicators
 * for support relationships, lazy-loaded collapsible sections for Members Council and Irish Funds Executive,
 * and comprehensive modal system for group previews with future feature announcements.
 * 
 * Performance Optimizations:
 * - Lazy loading for Members Council and Irish Funds Executive data
 * - Data fetching only occurs when sections are expanded
 * - Loading states for smooth user experience
 * - Backwards compatibility with optional props
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.workingGroupsdata - Working groups data object
 * @param {Array} props.workingGroupsdata.wgGroups - Array of working group categories
 * @param {Array} props.workingGroupsdata.wgSteeringGroups - Array of steering groups
 * @param {Array} props.workingGroupsdata.wgSubGroups - Array of sub-groups
 * @param {Array} [props.membersCouncilData] - Optional Members Council data array (will be lazy loaded if not provided)
 * @param {Array} [props.irishFundsExecutivesData] - Optional Irish Funds Executive data array (will be lazy loaded if not provided)
 * @returns {JSX.Element} The WGVisual component
 * 
 * @example
 * // With lazy loading (recommended for performance)
 * <WGVisual workingGroupsdata={workingGroupsHierarchy} />
 * 
 * @example
 * // With preloaded data (backwards compatibility)
 * <WGVisual 
 *   workingGroupsdata={workingGroupsHierarchy} 
 *   membersCouncilData={councilMembers}
 *   irishFundsExecutivesData={executives}
 * />
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import LoadingSpinner from '../Common/LoadingSpinner';
import MembersCouncil from '../CouncilCorner/MembersCouncil';
import IrishFundsExecutives from '../CouncilCorner/IrishFundsExecutives';
import apiHelper from '../../utils/apiHelper';
import { FILE_NAMES } from '../../utils/constants';

// Special styling cases for specific working groups
const SPECIAL_GROUP_STYLES = {
  'Daily Funds Return': {
    backgroundColor: '#FFBC00',
    color: '#000000' 
  }
};

/**
 * Gets styling configuration for working groups with special cases
 * 
 * @param {string} groupTitle - Title of the working group
 * @param {string} fallbackBackColor - Default background color
 * @param {string} fallbackColor - Default text color
 * @returns {Object} Style object with backgroundColor and color properties
 */
const getGroupStyle = (groupTitle, fallbackBackColor, fallbackColor) => {
  const specialStyle = SPECIAL_GROUP_STYLES[groupTitle];
  if (specialStyle) {
    return {
      backgroundColor: specialStyle.backgroundColor,
      color: specialStyle.color
    };
  }
  return {
    backgroundColor: fallbackBackColor || "#FFFFFF",
    color: fallbackColor || "#000000"
  };
};

/**
 * Main component that visualizes working groups, steering groups, and their relationships
 * 
 * @param {Object} props - Component props containing working groups data and member information
 * @returns {JSX.Element} Complex visual hierarchy with interactive elements
 */
const WGVisual = ({ workingGroupsdata, membersCouncilData = null, irishFundsExecutivesData = null }) => {
  const navigate = useNavigate();
  const showWorkingGroups = process.env.REACT_APP_SHOW_WORKING_GROUPS === 'true';
  
  // Lazy loading state
  const [sectionStates, setSectionStates] = useState({
    membersCouncil: { isOpen: false, isLoaded: false, isLoading: false },
    irishFundsExecutives: { isOpen: false, isLoaded: false, isLoading: false }
  });
  
  // Data state - use props if provided, otherwise fetch on demand
  const [lazyData, setLazyData] = useState({
    membersCouncil: membersCouncilData,
    irishFundsExecutives: irishFundsExecutivesData
  });
  
  const [previewModal, setPreviewModal] = React.useState({
    show: false,
    data: null,
    type: 'workingGroup' // 'workingGroup' or 'steeringGroup'
  });

    /**
     * Shows preview modal for working or steering groups
     * 
     * @param {Object} groupData - Group data object with Title and ID
     * @param {string} type - Type of group ('workingGroup' or 'steeringGroup')
     */
    const showPreviewModal = (groupData, type = 'workingGroup') => {
        setPreviewModal({
            show: true,
            data: groupData,
            type: type
        });
    };

    /**
     * Closes the preview modal and resets state
     */
    const closePreviewModal = () => {
        setPreviewModal({
            show: false,
            data: null,
            type: 'workingGroup'
        });
    };

    /**
     * Handles click on working group - either navigates to page or shows preview modal
     * based on REACT_APP_SHOW_WORKING_GROUPS environment variable
     * 
     * @param {Object} groupData - Group data object with Title and ID
     * @param {string} type - Type of group ('workingGroup' or 'steeringGroup')
     */
    const handleGroupClick = (groupData, type = 'workingGroup') => {
        if (showWorkingGroups && type === 'workingGroup' && groupData.ID) {
            // Navigate to the working group page
            navigate(`/WorkingGroups/WorkingGroup/${groupData.ID}`);
        } else if (showWorkingGroups && type === 'steeringGroup' && groupData.ID) {
            navigate(`/WorkingGroups/SteeringGroup/${groupData.ID}`);
        } else {
            // Show preview modal
            showPreviewModal(groupData, type);
        }
    };

    /**
     * Fetches Members Council data if not already loaded
     */
    const fetchMembersCouncilData = async () => {
        try {
            setSectionStates(prev => ({
                ...prev,
                membersCouncil: { ...prev.membersCouncil, isLoading: true }
            }));

            const result = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEMBERS_COUNCIL);
            
            setLazyData(prev => ({
                ...prev,
                membersCouncil: result.fileContent
            }));

            setSectionStates(prev => ({
                ...prev,
                membersCouncil: { 
                    ...prev.membersCouncil, 
                    isLoaded: true, 
                    isLoading: false 
                }
            }));
        } catch (error) {
            console.error('Error fetching Members Council data:', error);
            setSectionStates(prev => ({
                ...prev,
                membersCouncil: { ...prev.membersCouncil, isLoading: false }
            }));
        }
    };

    /**
     * Fetches Irish Funds Executives data if not already loaded
     */
    const fetchIrishFundsExecutivesData = async () => {
        try {
            setSectionStates(prev => ({
                ...prev,
                irishFundsExecutives: { ...prev.irishFundsExecutives, isLoading: true }
            }));

            const result = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.IRISH_FUNDS_EXECUTIVES);
            
            setLazyData(prev => ({
                ...prev,
                irishFundsExecutives: result.fileContent
            }));

            setSectionStates(prev => ({
                ...prev,
                irishFundsExecutives: { 
                    ...prev.irishFundsExecutives, 
                    isLoaded: true, 
                    isLoading: false 
                }
            }));
        } catch (error) {
            console.error('Error fetching Irish Funds Executives data:', error);
            setSectionStates(prev => ({
                ...prev,
                irishFundsExecutives: { ...prev.irishFundsExecutives, isLoading: false }
            }));
        }
    };

    /**
     * Handles toggle of details sections for lazy loading
     * 
     * @param {string} section - Section name ('membersCouncil' or 'irishFundsExecutives')
     * @param {boolean} isOpen - Whether the section is being opened
     */
    const handleSectionToggle = (section, isOpen) => {
        setSectionStates(prev => ({
            ...prev,
            [section]: { ...prev[section], isOpen }
        }));

        // If opening and data not loaded yet, fetch it
        if (isOpen && !sectionStates[section].isLoaded && !lazyData[section]) {
            if (section === 'membersCouncil') {
                fetchMembersCouncilData();
            } else if (section === 'irishFundsExecutives') {
                fetchIrishFundsExecutivesData();
            }
        }
    };

    // Update lazy data when props change (for backwards compatibility)
    useEffect(() => {
        if (membersCouncilData && !lazyData.membersCouncil) {
            setLazyData(prev => ({
                ...prev,
                membersCouncil: membersCouncilData
            }));
            setSectionStates(prev => ({
                ...prev,
                membersCouncil: { ...prev.membersCouncil, isLoaded: true }
            }));
        }
        
        if (irishFundsExecutivesData && !lazyData.irishFundsExecutives) {
            setLazyData(prev => ({
                ...prev,
                irishFundsExecutives: irishFundsExecutivesData
            }));
            setSectionStates(prev => ({
                ...prev,
                irishFundsExecutives: { ...prev.irishFundsExecutives, isLoaded: true }
            }));
        }
    }, [membersCouncilData, irishFundsExecutivesData, lazyData.membersCouncil, lazyData.irishFundsExecutives]);

    // Return early if data is empty or undefined
    if (!workingGroupsdata || Object.keys(workingGroupsdata).length === 0) {
        return <LoadingSpinner />;
    }
    // Destructure the data props into their respective group arrays
    const wgGroups = workingGroupsdata.wgGroups;

    
    const wgSteeringGroups = workingGroupsdata.wgSteeringGroups;
    const wgSubGroups = workingGroupsdata.wgSubGroups;

    return (
        <>
            {/* Preview Modal */}
            {previewModal.show && (
                <div className="preview-modal-overlay" onClick={closePreviewModal}>
                    <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-modal-header">
                            <h3>{previewModal.data?.Title}</h3>
                            <button className="preview-modal-close" onClick={closePreviewModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="preview-modal-body">
                            <div className="preview-badge">
                                <i className="fas fa-flask"></i>
                                Preview Mode
                            </div>
                            <p>
                                {previewModal.type === 'steeringGroup' 
                                    ? 'Steering Group details and functionality will be available in September 2025.' 
                                    : 'Working Group details including Terms of Reference, Meeting Minutes, Team Members, and Reference Documents will be available in September 2025.'
                                }
                            </p>
                            <div className="preview-features">
                                <h4>Coming Soon:</h4>
                                <ul>
                                    {previewModal.type === 'workingGroup' ? (
                                        <>
                                            <li><i className="fas fa-file-alt"></i> Terms of Reference</li>
                                            <li><i className="fas fa-users"></i> Meet the Team</li>
                                            <li><i className="fas fa-calendar"></i> Meeting Minutes</li>
                                            <li><i className="fas fa-folder"></i> Reference Documents</li>
                                        </>
                                    ) : (
                                        <>
                                            <li><i className="fas fa-compass"></i> Steering Committee Details</li>
                                            <li><i className="fas fa-users"></i> Committee Members</li>
                                            <li><i className="fas fa-bullseye"></i> Strategic Objectives</li>
                                        </>
                                    )}
                                </ul>
                            </div>
                        </div>
                        <div className="preview-modal-footer">
                            <button className="btn btn-secondary" onClick={closePreviewModal}>
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="pb-6 text-center">
                <details 
                    className="wg-details text-center"
                    onToggle={(e) => handleSectionToggle('membersCouncil', e.target.open)}
                >
                    <summary className="wg-summary text-center">
                        <div className="text-center full-width">
                            <span className="text-uppercase main-on-green-header white-text text-center">Members Council</span>
                        </div>
                    </summary>
                    <div className="wg-content">
                        {sectionStates.membersCouncil.isLoading ? (
                            <LoadingSpinner text="Loading Members Council..." />
                        ) : lazyData.membersCouncil ? (
                            <MembersCouncil data={lazyData.membersCouncil} />
                        ) : (
                            <div className="text-center py-4">
                                <p>Click to load Members Council</p>
                            </div>
                        )}
                    </div>
                </details>
            </div>

            <details onToggle={(e) => handleSectionToggle('irishFundsExecutives', e.target.open)}>
                <summary className="wg-summary text-center">
                    <div className="full-width">
                        <span className="text-uppercase main-on-green-header white-text">Irish Funds Executive</span>
                    </div>
                </summary>
                <div className="wg-content">
                    {sectionStates.irishFundsExecutives.isLoading ? (
                        <LoadingSpinner text="Loading Irish Funds Executive..." />
                    ) : lazyData.irishFundsExecutives ? (
                        <IrishFundsExecutives data={lazyData.irishFundsExecutives} />
                    ) : (
                        <div className="text-center py-4">
                            <p>Click to load Irish Funds Executive</p>
                        </div>
                    )}
                </div>
            </details>
            <div className="wg-visual mt-15">
                {/* Main loop that renders each group (Working, Steering, Project, etc.) */}
                {wgGroups.map(group => (
                    <React.Fragment key={group.ID}>
                        {/* Double arrows for Working Groups, single arrow for others */}
                        {group.GroupType === "WorkingGroups" ? (
                            <>
                                <div className="wg-group-wrapper">
                                    <div className="wg-group-up-arrows">
                                        <Tippy content="Escalation Support" placement="top">
                                            <div>
                                                <div className="arrow-up grey-arrow"></div>
                                            </div>
                                        </Tippy>
                                        <Tippy content="IF (and Council) Support" placement="top">
                                            <div>
                                                <div className="arrow-up black-arrow"></div>
                                            </div>
                                        </Tippy>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="wg-group-wrapper">
                                <div className={`other-group-up-arrows ${group.GroupType === "Low" ? "lower-arrow" : ""}`}>
                                    <Tippy content={group.GroupType === "Steering" || group.GroupType === "Low" ? "IF (and Council) Support" : "Escalation Support"} placement="top">
                                        <div>
                                            <div
                                                className={`arrow-up ${group.GroupType === "Steering" || group.GroupType === "Low" ? "black-arrow" : "grey-arrow"}`}
                                            ></div>
                                        </div>
                                    </Tippy>
                                </div>
                            </div>
                        )}
                        {/* Container for each group with dynamic styling based on group type */}
                        <div
                            className={`
                         ${group.GroupType === 'WorkingGroups' ? 'wg-group-working' : 'wg-group'} 
                         ${group.GroupType === 'Low' ? 'wg-group-lower' : ''} 
                         ${group.ArrowLeft === 'true' ? 'wg-group-left-arrow' : ''}
                         ${group.GroupType === "Steering" ? "wg-group-steering" : ""}
                     `}
                        >
                            {/* Group header with custom background and text colors */}
                            <div key={`group-header-${group.ID}`} style={{ backgroundColor: group.BackColor, color: group.Color }} className="wg-group-header">
                                <h2>{group.Title}</h2>
                            </div>

                            <div className="wg-subgroups">
                                {/* Working Groups specific layout - splits subgroups into two columns */}
                                {group.GroupType === "WorkingGroups" && (
                                    <>
                                        {["NoSteeringGroupID", "HasSteeringGroupID"].map((type, index) => {
                                            // Filter subgroups based on whether they have a steering group association
                                            const filteredSubGroups = wgSubGroups.filter(subGroup =>
                                                subGroup.WorkingGroup === group.Title &&
                                                (type === "NoSteeringGroupID" ? !subGroup.SteeringGroupID : subGroup.SteeringGroupID)
                                            );

                                            return (
                                                <div
                                                    key={type}
                                                    className={`${type === "NoSteeringGroupID" ? "left-container" : "right-container"}`}
                                                >
                                                    {/* Render each subgroup with tooltip and preview modal */}
                                                    {filteredSubGroups.map((subGroup) => (
                                                        <div key={subGroup.ID}>
                                                            <div className="preview-nav-link" onClick={() => handleGroupClick(subGroup, 'workingGroup')}>
                                                                <Tippy content={showWorkingGroups ? `View ${subGroup.Title}` : `Preview ${subGroup.Title}`} placement="right">
                                                                                                                            <span
                                                            className="wg-subgroup wg-subgroup-clickable"
                                                            style={getGroupStyle(subGroup.Title, subGroup.BackColor, subGroup.Color)}
                                                        >
                                                            <span className="wg-subgroup-text">{subGroup.Title}</span>
                                                            <i className={`fas ${showWorkingGroups ? 'fa-arrow-right' : 'fa-eye'} preview-icon`}></i>
                                                        </span>
                                                                </Tippy>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}

                                {/* Layout for non-Working Groups - single column display */}
                                {group.GroupType !== "WorkingGroups" && wgSubGroups.filter(subGroup => subGroup.WorkingGroup === group.Title).map(subGroup => (
                                        subGroup.WorkingGroupId == 5 ? (
                                        <span className="wg-subgroup"
                                            style={{
                                                display: 'inline-block',
                                                ...getGroupStyle(subGroup.Title, subGroup.BackColor, subGroup.Color)
                                            }}
                                        >
                                            <span>{subGroup.Title}</span>
                                        </span>
                                    ) : (
                                        <div className="preview-nav-link" onClick={() => handleGroupClick(subGroup, 'workingGroup')}>
                                            <span className="wg-subgroup wg-subgroup-clickable"
                                                style={getGroupStyle(subGroup.Title, subGroup.BackColor, subGroup.Color)}
                                            >
                                                <Tippy key={subGroup.ID} content={showWorkingGroups ? `View ${subGroup.Title}` : `Preview ${subGroup.Title}`} placement="left">
                                                    <span className="wg-subgroup-text">{subGroup.Title}</span>
                                                </Tippy>
                                                <i className={`fas ${showWorkingGroups ? 'fa-arrow-right' : 'fa-eye'} preview-icon`}></i>
                                            </span>
                                        </div>
                                    )
                                ))}

                                {/* Special layout for Steering Groups */}
                                {group.GroupType === "Steering" && wgSteeringGroups.map(steeringGroup => (
                                    <Tippy key={steeringGroup.ID} content={`Preview ${steeringGroup.Title} Steering Group`} placement="left">
                                        <div className="preview-nav-link" onClick={() => handleGroupClick(steeringGroup, 'steeringGroup')}>
                                            <span key={steeringGroup.ID} className="wg-subgroup wg-subgroup-steering wg-subgroup-clickable"
                                                style={getGroupStyle(steeringGroup.Title, steeringGroup.BackColor, steeringGroup.Color)}
                                            >
                                                <span className="wg-subgroup-text">
                                                    {steeringGroup.Title.split('&').map((part, index) => (
                                                        <React.Fragment key={index}>
                                                            {part.trim()}
                                                            {index < steeringGroup.Title.split('&').length - 1 && (
                                                                <>
                                                                    <br />
                                                                    &
                                                                    <br />
                                                                </>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </span>
                                                 <i className={`fas ${showWorkingGroups ? 'fa-arrow-right' : 'fa-eye'} preview-icon`}></i>
                                            </span>
                                        </div>
                                    </Tippy>
                                ))}
                            </div>

                            {group.GroupType === "WorkingGroups" && (
                                <div className="legend-container">
                                    <p>
                                        This diagram shows the relationships between different working groups and their steering committees.
                                        {showWorkingGroups 
                                            ? 'Click on any working group to view its details.' 
                                            : 'Click on any group to preview what will be available in September 2025.'
                                        }
                                    </p>
                                    <br />
                                    <div className="legend-row">
                                        <div className="legend-item">
                                            <div className="arrow-up grey-arrow"></div>
                                            <span>Escalation Support</span>
                                        </div>

                                    </div>

                                    <div className="legend-row">
                                        <div className="legend-item">
                                            <div className="arrow-up black-arrow"></div>
                                            <span>IF (and Council) Support</span>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

export default WGVisual;
